import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Avatar from '@/components-latest/primitives/Avatar'
import { PhotoCamera } from '@/components-latest/primitives/Icons'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'

import Styles from './AvatarInput.module.scss'
import { allowedImagesMimeTypes, validateFilesByMimeTypes } from './helpers'

export type IAvatarInputProps = {
  value?: File | null | string
  onChange?: (value: File | string | null) => void
  placeholderString: string
  isLoading?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>

const AvatarInput = React.forwardRef<HTMLInputElement, IAvatarInputProps>(
  ({
    onChange,
    value,
    disabled,
    placeholderString,
    isLoading,
    ...restProps
  }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [displayImageURL, setDisplayImageURL] = useState('')

    useEffect(() => {
      if (typeof value === 'string') {
        setDisplayImageURL(value)
      }
      if (value instanceof File) {
        const reader = new FileReader()
        reader.onload = readerEvent => {
          setDisplayImageURL(readerEvent.target!.result as string)
        }
        reader.onerror = () => {
          setDisplayImageURL('')
        }
        reader.readAsDataURL(value)
      }
    }, [value, setDisplayImageURL])

    const onFileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement
        const imageFile = target.files?.[0] || null

        if (!imageFile) return

        if (!validateFilesByMimeTypes([imageFile], allowedImagesMimeTypes)) {
          dispatch(
            addSystemNotification({
              message: t('common.avatar-input.format-eror', {
                imageName: imageFile.name || ''
              }),
              intent: 'error',
              duration: 3000
            })
          )
          return
        }

        if (imageFile && imageFile.size > 26214400) {
          dispatch(
            addSystemNotification({
              message: t('common.avatar-input.size-eror', {
                imageName: imageFile.name || ''
              }),
              intent: 'error',
              duration: 3000
            })
          )
          return
        }

        onChange?.(imageFile as File | null | string)
      },
      [onChange, dispatch]
    )

    const inputRef = useRef<HTMLInputElement>(null)
    const onOpenFileInput = () => {
      if (disabled) return
      inputRef.current?.click()
    }

    return (
      <div className={Styles.wrapper}>
        <Avatar
          isLoading={isLoading}
          image={displayImageURL}
          size="extraLarge"
          string={placeholderString}
        />
        <div
          className={Styles.inputWrapper}
          data-testid="avatar-input-button"
          onClick={onOpenFileInput}>
          <PhotoCamera />
          <input
            type="file"
            value=""
            disabled={disabled}
            data-testid="avatar-input"
            className={Styles.input}
            onChange={onFileChange}
            ref={inputRef}
            accept={allowedImagesMimeTypes.join(', ')}
            {...restProps}
          />
        </div>
      </div>
    )
  }
)

AvatarInput.displayName = 'AvatarInput'
export default AvatarInput
